import { lazy, Suspense, useEffect, ReactNode, useMemo, useCallback, useRef } from 'react'
import { clarity } from 'react-microsoft-clarity'
import { matchPath, useLocation, useNavigate, useSearchParams } from 'react-router'
import useMediaQuery from '@mui/material/useMediaQuery'
import { UserRole } from '@common/interfaces/user'
import classnames from 'classnames'
import genericSs from '../../assets/styles/generic.module.scss'
import AppShortcutsProvider from '../../components/AppShortcuts'
import { ArcPresenceLayer } from '../../components/ArcPresence'
import { ArcCallApprovals, ArcVoiceCallPill } from '../../components/ArcVoice'
import AtlasPreviewCard from '../../components/AtlasPreviewCard'
import BoxViewer from '../../components/BoxViewer'
import Icon from '../../components/Common/Icon'
import OptionalBoundary from '../../components/Common/OptionalBoundary'
import Text from '../../components/Common/Text'
import DynamicEnvFeedback from '../../components/DynamicEnvFeedback'
import FeedbackWidget from '../../components/FeedbackWidget'
import Header from '../../components/Header'
import HeaderClient from '../../components/HeaderClient'
import { HuddleInviteToast, HuddlePill } from '../../components/Huddle'
import Loader from '../../components/Loader'
import MobileHeader from '../../components/MobileHeader'
import MobileNav from '../../components/MobileNav'
import PullToRefresh from '../../components/PullToRefresh'
import RightSidebar from '../../components/RightSidebar'
import SideMenu from '../../components/SideMenu'
import { resolveRoleMenuItems } from '../../components/SideMenu/resolveRoleMenuItems'
import SideMenuClient from '../../components/SideMenuClient'
import { SIDE_MENU_CLIENT_USER } from '../../components/SideMenuClient/SideMenuConfig'
import config from '../../config'
import { ROUTES, isBlankLayoutRoute } from '../../constants/routes'
import { ArcVoiceCallProvider } from '../../contexts/ArcVoiceCallContext'
import { HuddleProvider } from '../../contexts/HuddleContext'
import { ArcScreenSection, arcScreenProps } from '../../helpers/arcVoice/screenText'
import { isInAtlasModuleFrame } from '../../helpers/atlasModuleFrame'
import { currentWorkFlow } from '../../helpers/helpers'
import {
  NOTES_DESKTOP_MIN_WIDTH,
  NotesPanelState,
  NotesPanelTab,
  getNotesPanelPreference,
  isFlagsCapableWorkflow,
  isNotesColumnSuppressed,
  notesPanelTabFromParam,
  resolveNotesDefault,
} from '../../helpers/notesPanel'
import { usePermissions } from '../../helpers/permissionContext'
import { writeStoredString } from '../../helpers/storedFlag'
import { useEdgeSwipeBack } from '../../hooks/useEdgeSwipeBack'
import { useIsMobile, useIsTouch } from '../../hooks/useIsMobile'
import { useNeedsBackGesture } from '../../hooks/useNeedsBackGesture'
import { useRecordVisit } from '../../hooks/useRecordVisit'
import useVisualViewport from '../../hooks/useVisualViewport'
import styles from './MainLayout.module.scss'
import { PropsFromRedux } from './index'

/** Read by the inline loading frame in index.html; keep the two in step. */
const BOOT_FRAME_STORAGE_KEY = 'atlas-boot-frame'

const NAV_RAIL_ATTRIBUTE = 'data-nav-rail'
const MODULE_FRAME_ATTRIBUTE = 'data-module-frame'

// Lazy because it pulls in the whole Messages page (draft-js, emoji picker, xlsx) and every
// Atlas page mounts this layout — including phones, where the bubble never renders.
const MessagesBubble = lazy(() => import('../../components/MessagesBubble'))
const ArcLivePanel = lazy(() => import('../../components/ArcLive/ArcLivePanel'))

export interface IProps extends PropsFromRedux {
  children: ReactNode
}

const MainLayout = ({
  isInitialized,
  onInit,
  children,
  role,
  setNotesPanelState,
  forceOpenNotesPanel,
  clearNotesPanelForce,
  isNotesPanelForced,
  isSidebarOpen,
  isNotesShown,
  isAdminLoggedAsClient,
  logoutAsClient,
  clientInfo,
  isProspectUser,
  isIntakeProspectClient,
  user,
  listChannels,
  listConversationGroups,
  listRecentConversations,
  countUnreadTasks,
  listPresence,
  listFavoriteProjects,
  isLoading,
  isPastClient,
  isDueDiligenceClient,
}: IProps) => {
  useEffect(() => {
    if (!isInitialized && !isLoading) {
      void onInit()
    }
  }, [onInit, isInitialized, isLoading])

  const isMobile = useIsMobile()
  // One call for the whole app: it publishes CSS custom properties rather than
  // returning state, so every mobile surface that needs the keyboard-aware height
  // reads `--viewport-height` instead of subscribing separately. Desktop has no
  // software keyboard, so the listener is pure cost there.
  useVisualViewport(isMobile)
  // matchMedia at the 1440 boundary (fires only on threshold cross, no
  // per-pixel re-render) — the notes default flips between docked and rail here.
  const isDesktop = useMediaQuery(`(min-width:${NOTES_DESKTOP_MIN_WIDTH}px)`)

  const { isParticipant, isAdmin, isPortfolio, isBDO, isMessagingUser, isTaskUser } =
    usePermissions()
  // Inside AIM's module panel the page is the panel's body: AIM draws the chrome around it,
  // and its badges, so the frame skips the fetches that only seed them.
  const isModuleFrame = isInAtlasModuleFrame()

  // Fetched here rather than only on the Messages page, because the nav badge has
  // to be right on every *other* page — that's the whole point of it. One request
  // per session is enough: the CHANNEL_MESSAGE_CREATED / CHANNEL_READ_STATE_CHANGED
  // WebSocket events keep the counts current from here on, so there's no polling.
  //
  // All three, because `totalUnreadIndicator` now sums `recentConversations` —
  // the only slice that carries every surface at once. The two lists stay: they
  // draw the sidebar the badge links to. Seeding just the lists left the badge at
  // zero on every non-AIM route until a message arrived over the socket, because
  // the reconnect resync is gated on `hasConnectedBefore` and so never runs on a
  // first connect.
  useEffect(() => {
    // `isMessagingUser`, not `isInternalUser`: the tasks-and-messaging role is not
    // internal, and messaging is one of the two things it has — gating on
    // internal here would leave its unread badge permanently empty.
    if (isMessagingUser && !isModuleFrame) {
      void listChannels()
      void listConversationGroups()
      void listRecentConversations()
    }
  }, [
    listChannels,
    listConversationGroups,
    listRecentConversations,
    isMessagingUser,
    isModuleFrame,
  ])

  // Same reasoning as the channels fetch above: the Tasks nav badge has to be
  // right on every page, so it's seeded once here rather than only on the My
  // Tasks page. TASK_UNREAD_COUNT_CHANGED WebSocket events keep it current from
  // here on, so there's no polling. The endpoint counts the caller's own tasks,
  // so `user?.id` is only a signed-in guard, not a parameter.
  // `isTaskUser` for the same reason `isMessagingUser` is used above: this role
  // has Tasks, so it has a Tasks badge to keep right.
  useEffect(() => {
    if (isTaskUser && user?.id && !isModuleFrame) {
      void countUnreadTasks()
    }
  }, [countUnreadTasks, isTaskUser, user?.id, isModuleFrame])

  // Presence dots and status badges render on assignee pickers and mention lists
  // app-wide, so the online set is seeded here rather than on the Messages page.
  // PRESENCE_CHANGED pushes keep it current afterwards — but only ever report a
  // *transition*, so without this a tab that loads while everyone is already
  // connected would show the whole company as offline.
  useEffect(() => {
    if (isMessagingUser && user?.id) {
      void listPresence()
    }
  }, [listPresence, isMessagingUser, user?.id])

  // Same reasoning as the two fetches above: the sidebar's Tasks link points at
  // whichever tab the user was last on, including a favorited project, so the
  // favorites have to be loaded on every page — not just the Tasks pages. Cheap
  // (id + name only) and seeded once; toggling a star updates the slice directly.
  useEffect(() => {
    if (isTaskUser && user?.id) {
      void listFavoriteProjects()
    }
  }, [listFavoriteProjects, isTaskUser, user?.id])

  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  // When puppeteer is taking the bank-export PDF (`?snapshot=true`) the
  // app chrome — side menu, header, right sidebar, floating AI chat,
  // Box viewer — is hidden so the captured PDF only contains the
  // amendment analysis content itself.
  const isSnapshotMode = searchParams.get('snapshot') === 'true'
  const hasAppChrome = !isSnapshotMode && !isModuleFrame
  const hasAimBubble = isMessagingUser && hasAppChrome && !isMobile
  const workflow = useMemo(
    () => currentWorkFlow(pathname, isPortfolio, isAdmin, isBDO),
    [isPortfolio, isAdmin, isBDO, pathname],
  )

  const isNotesSuppressed = isNotesColumnSuppressed(role, workflow)

  // Messages is a full-viewport surface: it pins itself to 100dvh and brings its
  // own chrome (sidebar, header, panels), so the page gutters below don't read as
  // breathing room — they read as a gap between the nav rail and the conversation
  // list. Full-bleed drops them for this route only.
  const isFullBleed = pathname.startsWith(ROUTES.MESSAGES)

  // Which tab a deep link is asking for, or null if this page has none. A flag
  // link only counts where the sidebar actually offers a Flags tab — on the home
  // page flags are a full-page view, so opening the panel there would be noise.
  const notesDeepLinkTab = useMemo(() => {
    const requestedTab = notesPanelTabFromParam(searchParams.get('panel'))
    // Only where the sidebar exists: Messages keeps its own `?panel=` for its side panels.
    if (requestedTab && workflow) {
      return requestedTab
    }
    if (searchParams.get('cellNoteContext') || searchParams.get('noteId')) {
      return NotesPanelTab.notes
    }
    if (searchParams.get('flagId') && isFlagsCapableWorkflow(workflow)) {
      return NotesPanelTab.flags
    }
    return null
  }, [searchParams, workflow])
  const isClientPortalUser = role === UserRole.CLIENT_USER

  // index.html draws the internal app frame while loading; it reads this to skip it for clients.
  useEffect(() => {
    if (role && !isModuleFrame) {
      writeStoredString(BOOT_FRAME_STORAGE_KEY, isClientPortalUser ? 'client' : 'internal')
    }
  }, [role, isClientPortalUser, isModuleFrame])

  // A force-open is scoped to the page that triggered it, so leaving the page
  // hands control back to the resolver. Declared before the latch effect below so
  // that navigating straight into a deep link clears, then re-latches.
  useEffect(() => {
    clearNotesPanelForce()
  }, [pathname, clearNotesPanelForce])

  // Latch the deep link. We must not derive "is forced open" from the URL on every
  // render: NotesContainer strips `?noteId=` as soon as it selects the note, which
  // would flip this back to false and collapse the panel while it is being read.
  // Latching once means the strip is invisible to the resolver.
  useEffect(() => {
    if (notesDeepLinkTab) {
      forceOpenNotesPanel(notesDeepLinkTab)
    }
  }, [notesDeepLinkTab, forceOpenNotesPanel])

  // The page a deep link last deferred the default on; see the in-place guard below.
  const deepLinkPathnameRef = useRef<string | null>(null)

  // Context-aware default for the notes right-column. Replaces the old effect
  // that force-reopened the panel on every navigation (why closing it never
  // stuck). The panel opens on note-heavy workflows on a desktop-class screen
  // (and for client-portal users, unchanged), and collapses to the rail
  // everywhere else. A user's explicit toggle is persisted in the header, not
  // here — this effect's deps don't change on a manual toggle, so it never
  // clobbers that choice.
  useEffect(() => {
    // Never fight a forced-open; the latch owns the state until it is cleared.
    // The live URL signal is checked alongside the latch because the force-open
    // effect above dispatches in this same commit: this effect's
    // `isNotesPanelForced` closure is still the pre-dispatch `false`, so on a deep
    // link's first render the latch alone would let the default resolve last and
    // collapse the panel that was just forced open. The re-render doesn't undo it
    // — the latch is set by then, so this effect only early-returns.
    if (isNotesPanelForced || notesDeepLinkTab) {
      if (notesDeepLinkTab) {
        deepLinkPathnameRef.current = pathname
      }
      return
    }
    const isDeepLinkEndingInPlace = deepLinkPathnameRef.current === pathname
    deepLinkPathnameRef.current = null
    if (!workflow || isParticipant || isNotesSuppressed) {
      setNotesPanelState(NotesPanelState.hidden)
      return
    }
    if (isMobile) {
      // Below md the column is a slide-in overlay opened from the header and the
      // rail is suppressed, so "collapsed" is the only resting state.
      setNotesPanelState(NotesPanelState.rail)
      return
    }
    // A deep link that ends on its own page (the panel's collapse button, a tab switch)
    // leaves the panel where the user put it; the default takes over on the next page.
    if (isDeepLinkEndingInPlace) {
      return
    }
    setNotesPanelState(
      resolveNotesDefault({
        workflow,
        isDesktop,
        persistedDefault: getNotesPanelPreference(workflow),
        isClientPortal: isClientPortalUser,
      }),
    )
  }, [
    // `pathname` (not just `workflow`) so a page that suppressed the column —
    // the legal-document viewer — gets it back on the next navigation even when
    // the workflow is unchanged. Re-resolving per page is safe now that the
    // resolver honours the remembered preference instead of forcing open.
    pathname,
    workflow,
    isDesktop,
    isNotesPanelForced,
    // Once NotesContainer strips `?noteId=` this goes null again, but by then the
    // latch above holds the panel — so the strip still never reaches the default.
    notesDeepLinkTab,
    isParticipant,
    isNotesSuppressed,
    isMobile,
    isClientPortalUser,
    setNotesPanelState,
  ])

  const handleLogoutAsClient = useCallback(async () => {
    if (isAdminLoggedAsClient) {
      await logoutAsClient()
    }
  }, [isAdminLoggedAsClient, logoutAsClient])

  useEffect(() => {
    if (user?.id && clarity.hasStarted()) {
      clarity.identify(user.email, {
        'friendly-name': `${user.firstName} ${user.lastName}`,
        role: user.role,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        clientName: user.clientName,
      })
    }
  }, [user])

  // Every navigation enters the back trail from here, so a page does not have to
  // opt in to be somewhere the user can get back to.
  useRecordVisit()

  const isBlankLayout = isBlankLayoutRoute(pathname)

  const isClientPortal = role === UserRole.CLIENT_USER && clientInfo !== null
  const isIntakeProspectPortal = isClientPortal && Boolean(isIntakeProspectClient)

  // A prospect's only destination is the data room, so a whole nav column for a
  // single link is noise — the intake's step rail serves as their sidebar until
  // the portal actually grows a second tab. This only ever applies to a prospect
  // portal session: ClientIntakePage redirects everyone else to the internal
  // prospect record rather than rendering the data room in the internal shell.
  const hasSideMenu = !isBlankLayout && !isIntakeProspectPortal
  // Whether the fixed nav rail is on screen, and how wide. Published on the document root as
  // `data-nav-rail` (→ `--nav-rail-width` in global.scss) because the rail's Drawer paper is
  // `position: fixed; left: 0`, so anything else pinned to the viewport's left edge — a
  // frozen table column, the portaled expanded task panel — has to start after it.
  const hasRail = hasSideMenu && hasAppChrome && !isMobile
  const railState = isSidebarOpen ? 'pinned' : 'collapsed'

  useEffect(() => {
    if (!hasRail) {
      return
    }
    document.documentElement.setAttribute(NAV_RAIL_ATTRIBUTE, railState)
    return () => document.documentElement.removeAttribute(NAV_RAIL_ATTRIBUTE)
  }, [hasRail, railState])

  // Zeroes the mobile chrome tokens in global.scss, which every fixed surface subtracts.
  useEffect(() => {
    if (!isModuleFrame) {
      return
    }
    document.documentElement.setAttribute(MODULE_FRAME_ATTRIBUTE, '')
    return () => document.documentElement.removeAttribute(MODULE_FRAME_ATTRIBUTE)
  }, [isModuleFrame])

  // The same menu the rails render, resolved once here so the tab bar and the rail can
  // never disagree about what a role can reach.
  const mobileNavItems = useMemo(
    () =>
      resolveRoleMenuItems({
        role,
        isClientPortal,
        isPastClient,
        isDueDiligenceClient,
        isAdminLoggedAsClient,
      }),
    [role, isClientPortal, isPastClient, isDueDiligenceClient, isAdminLoggedAsClient],
  )

  /**
   * Swipe in from the left edge to go back, on any page that was drilled into.
   *
   * Installed on Android there is no back gesture inside the app and no browser chrome to
   * fall back on, so every drill-down is a one-way trip unless the page happens to draw
   * its own control. That is the one case this exists for — see `useNeedsBackGesture`,
   * which is also why it stands down on iOS, where WebKit keeps its own edge swipe even
   * with the chrome gone.
   *
   * "Drilled into" is decided by the navigation rather than by a list of routes: a
   * destination the tab bar or the More sheet can reach is somewhere you land, and
   * anything else is somewhere you went from there. That keeps this in step with
   * `SideMenuConfig` for free, the same way the tab bar is.
   *
   * `history.state.idx` is React Router's own depth counter — at zero this entry is the
   * session's first, so a deep link opened cold has nothing behind it and the gesture
   * would leave the app.
   *
   * A surface that has taken over the screen (`useImmersiveSurface`) runs a back gesture
   * of its own, which knows what back means there. Nothing here excludes it: the inner
   * one claims the touch on the move that engages it, so this one never engages and
   * never navigates alongside it.
   */
  const navigate = useNavigate()
  const isTouch = useIsTouch()
  const needsBackGesture = useNeedsBackGesture()
  const navDestinations = useMemo(
    () =>
      mobileNavItems.flatMap((item) => [
        item.link,
        ...(item.activeRoutes ?? []),
        ...(item.subItems ?? []).map((subItem) => subItem.link),
      ]),
    [mobileNavItems],
  )
  const isNavDestination = useMemo(
    () => navDestinations.some((link) => matchPath({ path: link, end: true }, pathname)),
    [navDestinations, pathname],
  )
  const hasSomethingBehind = ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0
  const handleSwipeBack = useCallback(() => navigate(-1), [navigate])
  const { ref: swipeBackRef } = useEdgeSwipeBack({
    isEnabled: isMobile && isTouch && needsBackGesture && !isNavDestination && hasSomethingBehind,
    onBack: handleSwipeBack,
  })

  const isRouteInClientMenu = useMemo(() => {
    return SIDE_MENU_CLIENT_USER.some((menuItem) => {
      return matchPath({ path: menuItem.link, end: menuItem.link === '/' }, pathname)
    })
  }, [pathname])

  const SideMenuComponent = isClientPortal ? SideMenuClient : SideMenu
  const HeaderComponent = isClientPortal ? HeaderClient : Header
  const shouldShowLoader = !isInitialized || (role === UserRole.CLIENT_USER && clientInfo === null)
  const shouldUseClientLoader = isClientPortal && isRouteInClientMenu

  if (shouldShowLoader) {
    return <Loader isClientPortal={shouldUseClientLoader} />
  }
  return (
    // Above every page, so a voice call with Arc carries on as you move around the app.
    <ArcVoiceCallProvider>
      <HuddleProvider>
        <AppShortcutsProvider
          isEnabled={hasAppChrome && (isTaskUser || isMessagingUser)}
          hasAimBubble={hasAimBubble}
          canCreateTask={isTaskUser}
        >
          <div className={styles.container} data-theme={isClientPortal ? 'client' : 'internal'}>
            {/* The rails are desktop-only now: below md the header and the bottom tab bar are
          the navigation, and rendering a collapsed rail underneath them just stole width.
          MobileHeader moved out of SideMenu so it exists on every route — inside the rail
          it was missing wherever the rail was not rendered (blank-layout routes, snapshot
          mode, and due-diligence client users, whose SideMenu returns null).
          Both gate on `hasSideMenu` (master's flag: not a blank layout and not the intake
          prospect portal), so the mobile chrome appears exactly where the desktop rail
          would — a route with no app navigation gets neither. */}
            {hasRail && <SideMenuComponent />}
            {hasSideMenu && hasAppChrome && isMobile && <MobileHeader />}
            <div
              className={classnames(styles.content, {
                [styles.blankLayoutContent]: isBlankLayout,
                [styles.noSideMenuContent]: !hasSideMenu && !isBlankLayout,
                [styles.loggedAsClientContent]: isAdminLoggedAsClient,
                [styles.moduleFrameContent]: isModuleFrame,
              })}
            >
              <div
                ref={swipeBackRef}
                className={classnames(styles.childrenContent, {
                  [styles.childrenContentFullBleed]: isFullBleed,
                })}
                {...arcScreenProps(ArcScreenSection.Page)}
              >
                {/* Wraps the routed page rather than the whole shell: the gesture reads the
              document scroll, and the header and tab bar are fixed chrome that never
              takes part in it. PullToRefresh no-ops above `md`. */}
                <PullToRefresh>{children}</PullToRefresh>
              </div>

              <div
                id="multiSelectToolbar"
                className={classnames(genericSs.multiSelectToolbar, {
                  [genericSs.multiSelectToolbarWithSidebar]: isSidebarOpen,
                  [genericSs.multiSelectToolbarWithNotes]: isNotesShown,
                })}
              />

              <div id="multiSelectToolbarModal" className={genericSs.multiSelectToolbarModal} />
            </div>
            {/* The 75px desktop header used to mount on mobile too and simply be covered by
          MobileHeader's z-index. It carried the document-title effect, which MobileHeader
          now owns. `hasSideMenu` is master's new prop and is kept. */}
            {hasAppChrome && !isMobile && (
              <HeaderComponent workflow={workflow} hasSideMenu={hasSideMenu} />
            )}
            {isAdminLoggedAsClient && hasAppChrome && (
              <div
                className={classnames(styles.loginAsClientContainer, {
                  [styles.loginAsClientContainerExpanded]: isSidebarOpen,
                  [styles.loginAsClientContainerProspect]: isProspectUser,
                })}
              >
                {!isMobile && (
                  <span className={styles.loginAsClientIcon}>
                    <Icon name="alert-circle" size="lg" />
                  </span>
                )}
                <Text variant="body" component="span" casing="none">
                  You are viewing Atlas as <b>{clientInfo?.clientName}</b>. Click&nbsp;
                  <span
                    className={styles.loginAsClientContainerLink}
                    onClick={handleLogoutAsClient}
                  >
                    here
                  </span>
                  &nbsp;to go {isMobile ? 'back.' : 'back to your account.'}
                </Text>
              </div>
            )}

            {/* Messaging — and, since Arc moved inside a conversation, Arc — from any page. It
          hides itself on /messages, where the page owns the surface: only one copy of it
          may be mounted at a time.

          Desktop only. The bubble is a hover-era affordance: its FAB is fixed 20px off the
          bottom edge, which is inside the mobile tab bar's footprint (fixed at bottom: 0,
          and at a higher z-index, so the button lands behind the bar). Nothing is lost by
          dropping it there — Messages is its own entry in that tab bar for every internal
          role that has messaging (mobileNavConfig), and it opens the same surface
          full-screen, with the Arc conversations in its sidebar. A FAB over a full-screen
          conversation would be the wrong affordance for a destination that is already one
          tap away.

          Gated on `isMessagingUser` rather than `isInternalUser`: it hides itself on
          /messages, so the only pages it can appear on for the tasks-and-messaging role
          are Tasks and their settings tabs — exactly where every other role with messaging
          gets it. */}
            {hasAimBubble && (
              <OptionalBoundary>
                <Suspense fallback={null}>
                  <MessagesBubble />
                  <ArcLivePanel />
                </Suspense>
              </OptionalBoundary>
            )}

            {/* The same flags that mount the mobile chrome above: no header means no profile menu
          to open this from, and each bar that is mounted is space the panel gives up. */}
            {hasAppChrome && (
              <FeedbackWidget
                hasMobileHeader={hasSideMenu}
                hasMobileTabBar={!isBlankLayout && mobileNavItems.length > 0}
              />
            )}

            {config.isDynamicEnv && hasAppChrome && <DynamicEnvFeedback />}

            {!isSnapshotMode && <BoxViewer />}

            {!isSnapshotMode && <AtlasPreviewCard />}

            {!isBlankLayout && workflow && !isParticipant && hasAppChrome && !isNotesSuppressed && (
              <RightSidebar workflow={workflow} />
            )}

            {!isBlankLayout && hasAppChrome && isMobile && (
              <MobileNav role={role} items={mobileNavItems} />
            )}

            {isMessagingUser && hasAppChrome && <ArcVoiceCallPill canUseBubble={!isMobile} />}
            {isMessagingUser && hasAppChrome && <HuddlePill canUseBubble={!isMobile} />}
            {isMessagingUser && hasAppChrome && <HuddleInviteToast />}
            {isMessagingUser && hasAppChrome && <ArcCallApprovals />}
            {hasAppChrome && <ArcPresenceLayer />}
          </div>
        </AppShortcutsProvider>
      </HuddleProvider>
    </ArcVoiceCallProvider>
  )
}

export default MainLayout
